import {AfterViewInit, Component, ElementRef, Input, OnInit, OnDestroy, ViewChild} from '@angular/core';
import {
  CalendarOptions,
  DateSelectArg,
  EventClickArg, EventAddArg, EventInput,
  EventApi,
  FullCalendarComponent,
  Calendar, FullCalendarModule
} from '@fullcalendar/angular';
import { v4 as uuid } from 'uuid';
import {colors} from "../../../consts/colors";
import interactionPlugin, {Draggable} from "@fullcalendar/interaction";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import listPlugin from "@fullcalendar/list";
import {environment} from "../../../../environments/environment";
import {AppComponent} from "../../../app.component";
import {MobileContainerComponent} from "../mobile-container/mobile-container.component";
import {MatDialog, MatDialogConfig} from "@angular/material/dialog";
import {SharedDataService} from "../../../services/shared-data.service";
import {User} from "../../../objects/user";
import {FormatterInput} from "@fullcalendar/common";
import {formatDate} from "@angular/common";
import {DialogCanvasComponent} from "../../dialog-canvas/dialog-canvas.component";
import {AppEvent} from "../../../objects/appEvent";
import {SubscriptionLike} from "rxjs";
import {DAY, extractCalendarStep, getFullTwoWeeks} from "../../../utils/parseUtils";
import {ListItem} from "../../../objects/listItem";

@Component({
  selector: 'app-calendar-container',
  templateUrl: './calendar-container.component.html',
  styleUrls: ['./calendar-container.component.scss']
})
export class CalendarContainerComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('calendar', { static: false }) public calendarComponent: FullCalendarComponent;
  @ViewChild('calendarContainer') public calendarContainer: ElementRef<HTMLElement>;
  @ViewChild('externalEvents', { static: false }) public externalEventsRef: ElementRef<HTMLElement>;

  @Input() profile: User
  @Input() load_playhour_data: boolean
  @Input() allow_events_edit: boolean = false
  @Input() admin_view: boolean = false

  public calendarApi: Calendar;
  private draggable: Draggable;

  calendarOptions: CalendarOptions;
  currentEvents: EventApi[] = [];
  eventTemplates: EventInput[] = [];
  calendarDataLoaded: boolean = true;
  formatter: FormatterInput = {
    month: 'long',
    day: 'numeric',
  }

  constructor(public application: AppComponent,
              public mobileContainer: MobileContainerComponent,
              public dialog: MatDialog,
              public confirmationDialog: MatDialog,
              private sharedDataService: SharedDataService,) { }

  private sharedEventSubscription: SubscriptionLike;
  addSubscriptions() {
    this.sharedEventSubscription = this.sharedDataService.globalEventObservable$.subscribe(
      appEvent => {
        if (appEvent.type === AppEvent.EVENT_UPDATED && this.calendarDataLoaded) {
          if(this.admin_view) { //full refresh
            this.updateSchedule();
          } else {
            this.reflectEventChange(appEvent.data);
          }
        }
        if (appEvent.type === AppEvent.ADD_TRAINER_SCHEDULE && this.calendarDataLoaded) {
          this.addTrainerScheduleEvent(appEvent.data['trainerID']);
        }
        if (appEvent.type === AppEvent.ADD_SCHEDULE && this.calendarDataLoaded) {
          this.editScheduleEvent();
        }
        if (appEvent.type === AppEvent.SCHEDULE_ADDED) {
          this.addScheduleEvent(appEvent.data['bookingEvents']);
        }
        if (appEvent.type === AppEvent.SCHEDULE_UPDATED) {
          this.updateSchedule();
        }
      });
  }
  fromDate:Date = new Date();
  toDate:Date = getFullTwoWeeks();
  ngOnInit(): void {
    this.addSubscriptions()
    FullCalendarModule.registerPlugins([
      dayGridPlugin,
      timeGridPlugin,
      listPlugin,
      interactionPlugin
    ])

    let fromDate = this.fromDate;
    let toDate = this.toDate;

    // let weekDay = fromDate.getDay() ? fromDate.getDay() : 7; // 1-7 => Mon-Sun
    // let fullTwoWeeksTail = (weekDay == 1) ? 7 : 14;
    // let remainingWeekDays = (7-weekDay);
    // let toDate = new Date();
    // toDate.setTime(fromDate.getTime()+(remainingWeekDays+fullTwoWeeksTail)*24*3600*1000);

    this.calendarOptions = {
      headerToolbar: {
        //left: 'prev,today,next',
        left: 'prev',
        center: 'title',
        //right: 'dayGridMonth,timeGridWeek,timeGridDay,listWeek'
        right: 'next'
      },
      buttonText: {
        today: 'Today'
      },

      firstDay: 1, // 1 Monday
      validRange: function(nowDate: Date) {
        // let weekDay = nowDate.getDay() ? nowDate.getDay() : 7; // 1-7 => Mon-Sun
        // let fullTwoWeeksTail = (weekDay == 1) ? 7 : 14;
        // let remainingWeekDays = (7-weekDay);
        // return {
        //   start: nowDate,
        //   end: new Date().setTime(nowDate.getTime()+(remainingWeekDays+fullTwoWeeksTail)*24*3600*1000)
        // };
        return {
          start: fromDate,
          end: toDate
        };
      },
      businessHours: {
        // days of week. an array of zero-based day of week integers (0=Sunday)
        daysOfWeek: [ 0, 1, 2, 3, 4, 5, 6 ], // Monday - Thursday

        // startTime: '08:00', // a start time
        // endTime: '23:20', // an end time
        startTime: this.application.account.calendars[0]['parsed_params']['start_hour'], // a start time
        endTime: this.application.account.calendars[0]['parsed_params']['end_hour'], // an end time
      },
      displayEventTime: false,
      locale: 'uk',
      // slotDuration: '00:40:00',
      // slotMinTime: '08:00:00',
      // slotMaxTime: '23:20:00',
      //slotDuration: AppComponent.convertSecondsToHMS(parseInt(this.application.account.calendars[0]['parsed_params']['duration'])),
      slotDuration: AppComponent.convertSecondsToHMS(extractCalendarStep(this.application.account.calendars[0]['parsed_params']['duration'])),
      slotMinTime: this.application.account.calendars[0]['parsed_params']['start_hour']+':00',
      slotMaxTime: this.application.account.calendars[0]['parsed_params']['end_hour']+':00',

      allDaySlot: false,
      initialView: 'timeGridWeek',
      titleFormat: { month: 'long', day: 'numeric' },
      slotLabelFormat: {
        hour: 'numeric',
        minute: '2-digit',
        omitZeroMinute: false,
        meridiem: 'short'
      },
      initialEvents: [], // alternatively, use the `events` setting to fetch from a feed
      weekends: true,
      editable: false,
      selectable: false,
      selectMirror: true,
      dayMaxEvents: true,
      //select: this.handleDateSelect.bind(this),
      eventClick: this.handleEventClick.bind(this),
      //eventsSet: this.handleEventsSet.bind(this),
      //eventAdd:  this.handleEventAdd.bind(this),
      /* you can update a remote database when these fire:
      eventAdd:
      eventChange:
      eventRemove:
      */
    };
    // console.log(this.application.account.calendars[0]['parsed_params']['start_hour'])
    // console.log(this.application.account.calendars[0]['parsed_params']['end_hour'])
    // console.log(AppComponent.convertSecondsToHMS(parseInt(this.application.account.calendars[0]['parsed_params']['duration'])))
    this.updateSchedule();
  }
  private updateSchedule() {
    let fromDate = this.fromDate;
    let toDate = this.toDate;

    if(this.calendarApi) { //remove old events
      this.calendarApi.getEvents().forEach( event => {
        event.remove()
      })
    }

    this.calendarDataLoaded = false;
    this.application.apiService.getBookingData(
      fromDate.getFullYear()+'-'+(fromDate.getMonth()+1)+'-'+fromDate.getDate(),
      toDate.getFullYear()+'-'+(toDate.getMonth()+1)+'-'+toDate.getDate(),
      this.profile.id,
      this.load_playhour_data //load_playhour_data is deprecated 03042023
    ).subscribe(
      data => this.handleItems(data),
      error => this.handleItemsError(error))
  }
  ngOnDestroy(): void {
    this.sharedEventSubscription.unsubscribe();
  }

  public handleItems(data): void {
    this.calendarDataLoaded = true;
    setTimeout(() => {
      this.calendarApi = this.calendarComponent.getApi();
      //this.calendarApi.setOption('height','100%'); // doesn't work
      setTimeout(() => {
        this.calendarApi.setOption('height',this.calendarContainer.nativeElement.offsetHeight)
      })

      data.forEach(bookingEvent => {
        setTimeout(() => {
          this.calendarApi.addEvent(bookingEvent);
        })
      })
    })
    // setTimeout(() => {
    //   console.log('getEvents',this.calendarApi.getEvents());
    //   console.log('getEventSources',this.calendarApi.getEventSources());
    //   console.log('getEventById',this.calendarApi.getEventById('7895'));
    //   let ttEvent: EventApi = this.calendarApi.getEventById('7895');
    //   let ttDateEnd = ttEvent.end;
    //   ttEvent.setEnd(ttDateEnd.getTime()+7200*1000);
    //   ttEvent.remove();
    // },1000)
  }
  public handleItemsError(error): void {
    this.calendarDataLoaded = true;
    this.sharedDataService.pushNotification({ text: error.errMsg, type: 'error' })
  }

  public reflectEventChange(eventData:object): void {
    console.log('eventData',eventData);
    switch (eventData['changeAction']) {
      case 'training_cancel':
        // let eventsToRemove = this.calendarApi.getEvents().map(calendarEvent => {
        //   if(calendarEvent.extendedProps?.full_event_data?.id == eventData['item']['itemFullData']['id']) {
        //     calendarEvent.remove();
        //   }
        // });
        let calendarEvent: EventApi = this.calendarApi.getEventById(eventData['item']['itemFullData']['id']);
        calendarEvent?.remove();
        break;
    }
  }

  public addTrainerScheduleEvent(trainerID, eventItem:EventApi = null): void {
    const dialogRef = this.dialog.open(DialogCanvasComponent, {
      //...this.commonDialogConf,
      height: '50%',
      minHeight: '50%',
      minWidth: this.application.layoutMatches['isHandsetDefaultLayout'] ? '90%' : '65%',
      data: {
        application: this.application,
        state: 'edit-trainer-event',
        payload: {
          trainerID: trainerID,
          item: eventItem,
          calendarApi: this.calendarApi,
          // fromDate: this.fromDate,
          // toDate: this.toDate,
        }
      }
    } as MatDialogConfig);
    dialogRef.addPanelClass('common-dialog');
    dialogRef.addPanelClass('common-dialog-medium-form');
    dialogRef.afterOpened().subscribe(() => {
      (dialogRef.componentInstance as DialogCanvasComponent).isOpened = true;
    })
    dialogRef.afterClosed()
      .subscribe((confirmed) => {
      })
  }

  public editScheduleEvent(eventItem:EventApi = null): void {
    console.log('editScheduleEvent',eventItem);
    if(eventItem?.extendedProps?.schedule_type == 'training') { //todo: training or booking
      //compatible with event-listing-item.ts component itemOpen()
      //let eventPayloadParsed = JSON.parse(eventItem.extendedProps['payload']);
      let payloadItem = new ListItem();
      payloadItem.itemFullData = eventItem.extendedProps['full_event_data'];
      payloadItem.itemFullData['payload_parsed'] = JSON.parse(eventItem.extendedProps['full_event_data']['payload']);
      const dialogRef = this.dialog.open(DialogCanvasComponent, {
        //...this.commonDialogConf,
        height: '50%',
        minHeight: '50%',
        minWidth: this.application.layoutMatches['isHandsetDefaultLayout'] ? '90%' : '65%',
        data: {
          application: this.application,
          state: 'event-open',  //opens app-event-item-view
          payload: {item: payloadItem},
        }
      } as MatDialogConfig);
      dialogRef.addPanelClass('common-dialog');
      dialogRef.addPanelClass('common-dialog-medium-form');
      dialogRef.afterOpened().subscribe(() => {
        (dialogRef.componentInstance as DialogCanvasComponent).isOpened = true;
      })
    } else {
      const dialogRef = this.dialog.open(DialogCanvasComponent, {
        //...this.commonDialogConf,
        height: '50%',
        minHeight: '50%',
        minWidth: this.application.layoutMatches['isHandsetDefaultLayout'] ? '90%' : '65%',
        data: {
          application: this.application,
          state: 'schedule-calendar-event',
          payload: {
            item: eventItem,
            calendarApi: this.calendarApi,
            // fromDate: this.fromDate,
            // toDate: this.toDate,
          }
        }
      } as MatDialogConfig);
      dialogRef.addPanelClass('common-dialog');
      dialogRef.addPanelClass('common-dialog-medium-form');
      dialogRef.afterOpened().subscribe(() => {
        (dialogRef.componentInstance as DialogCanvasComponent).isOpened = true;
      })
      dialogRef.afterClosed()
        .subscribe((confirmed) => {
          console.log('confirmed',confirmed);
        })
    }
  }

  private addScheduleEvent(bookingEvents: object[]) {
    bookingEvents.forEach(bookingEvent => {
      setTimeout(() => {
        this.calendarApi.addEvent(bookingEvent);
      })
    })
  }

  public ngAfterViewInit(): void {
    // this.calendarApi = this.calendarComponent.getApi();
    // //this.calendarApi.setOption('height','100%'); // doesn't work
    // setTimeout(() => {
    //   this.calendarApi.setOption('height',this.calendarContainer.nativeElement.offsetHeight)
    // })

    let e1 = {
      id: uuid().toUpperCase(),
      title: 'All Day Event',
      start: (new Date()).getTime(),
      end: new Date().setTime(new Date().getTime()+2*24*3600*1000),
      allDay: false,
      backgroundColor: colors['RED'],
      textColor: '#fff',
      borderColor: 'transparent',
      extendedProps: {description: 'Will be busy throughout the whole day'}
    }
    //setTimeout(() => { this.calendarApi.addEvent(e1); })

    this.draggable = new Draggable(this.externalEventsRef.nativeElement, {
      itemSelector: '.external-event',
      eventData: function (el: HTMLElement) {
        let ret = JSON.parse(el.getAttribute('eventInput-json'));
        console.log('el.dataset',el.attributes['eventInput-title'],el.getAttribute('eventInput-json'),ret)
        return ret;
        return {
          id: uuid().toUpperCase(),
          title: el.innerText,
          proxySettings: el.attributes['proxySettings'],
          backgroundColor: el.attributes['backgroundColor']
        };
      }
    });

    this.eventTemplates.push({
      id: uuid().toUpperCase(),
      title: 'template 1',
      start: new Date(),
      end: new Date().setTime(new Date().getTime()+2*24*3600*1000),
      allDay: false,
      backgroundColor: colors.GREEN,
      textColor: '#fff',
      borderColor: 'transparent',
      extendedProps: {
        description: 'Will be busy throughout the whole day',
        proxySettings: {foo: 'bar'}
      }
    })
    this.eventTemplates.push({
      id: uuid().toUpperCase(),
      title: 'template 2',
      start: new Date(),
      end: new Date().setTime(new Date().getTime()+2*24*3600*1000),
      allDay: false,
      backgroundColor: colors.PINK,
      textColor: '#fff',
      borderColor: 'transparent',
      extendedProps: {
        description: 'Will be busy throughout the whole day',
        backgroundColor: colors.GREEN
      }
    })
  }

  eventInputJson(event: EventInput): string {
    return JSON.stringify(event);
  }

  handleDateSelect(selectInfo: DateSelectArg) {
    const title = prompt('Please enter a new title for your event');
    const calendarApi = selectInfo.view.calendar;

    calendarApi.unselect(); // clear date selection

    if (title) {
      calendarApi.addEvent({
        id: uuid().toUpperCase(),
        title,
        start: selectInfo.startStr,
        end: selectInfo.endStr,
        allDay: selectInfo.allDay
      });
    }
  }

  handleEventClick(clickInfo: EventClickArg) {
    if(!this.allow_events_edit) return;
    console.log('clickInfo.event',clickInfo.event)
    if(this.admin_view) {
      if(clickInfo.event?.extendedProps['schedule_type'] == 'training') {
        //console.log('this.eventPayload',this.eventPayload)
        this.editScheduleEvent(clickInfo.event)
      }
    } else {
      this.editScheduleEvent(clickInfo.event)
    }
    // if (confirm(`Are you sure you want to delete the event '${clickInfo.event.title}'`)) {
    //   clickInfo.event.remove();
    // }
  }
  handleEventAdd(addInfo: EventAddArg) {
    console.log('addInfo',addInfo)
  }
  handleEventsSet(events: EventApi[]) {
return;
    //if(!this.calendarApi) {
    if(1) {
      this.currentEvents = events;
      return;
    }
    let eventsViaAPI: EventApi[] = this.calendarApi.getEvents();
    if(eventsViaAPI.length) {
      let lastEvent = eventsViaAPI[eventsViaAPI.length-1];
      if(lastEvent.extendedProps['proxySettings']) {
        //proxy event split example
        lastEvent.remove();
        eventsViaAPI.pop();

        let e1 = {
          id: uuid().toUpperCase(),
          title: 'Dynamic event 1',
          start: new Date().setTime(new Date().getTime()-2*24*3600*1000),
          end: new Date().setTime(new Date().getTime()-2*24*3600*1000+2400*1000),
          allDay: false,
          backgroundColor: colors.DARK_BLUE,
          textColor: '#fff',
          borderColor: 'transparent',
          extendedProps: {description: 'Will be busy throughout the whole day'}
        }
        this.calendarApi.addEvent(e1)
        //setTimeout(() => { this.calendarApi.addEvent(e1); })

        let e2 = {
          id: uuid().toUpperCase(),
          title: 'Dynamic event 1',
          start: new Date().setTime(new Date().getTime()-1*24*3600*1000),
          end: new Date().setTime(new Date().getTime()-1*24*3600*1000+2400*1000),
          allDay: false,
          backgroundColor: colors.DARK_BLUE,
          textColor: '#fff',
          borderColor: 'transparent',
          extendedProps: {description: 'Will be busy throughout the whole day'}
        }
        this.calendarApi.addEvent(e2)
        //setTimeout(() => { this.calendarApi.addEvent(e2); })

      }
    }
    this.currentEvents = eventsViaAPI;
  }

}
